import { get } from 'lodash';
import { Dispatch, SetStateAction } from 'react';
import { AlternateIdInput, Organization } from 'sr-types/lib/organization/v1/graphql';
import { Person } from 'sr-types/lib/person/v1/graphql';
import { OrganizationEditorInput } from '../../../organization/organization';
import { BasicInfoInputExtended } from '../../../person/detailsPage/detailsPageUtilities/personBasicInformation';
import { ValidationRulesType } from '../../../production/helpers/productionUtils';
import {
    DOMAIN_ERROR,
    IMDB_HOST,
    IMDB_LABEL,
    IMDB_ME_HOST,
    IMDB_PRO_HOST,
    LINKEDIN_HOST,
    LINKEDIN_LABEL,
    LINKEDIN_REGEX,
    PROFILE_CONFLICT_ERROR,
    ROOT_DOMAIN_REGEX,
    STUDIO_SYSTEM_HOST,
    STUDIO_SYSTEM_LABEL,
    UNPARSABLE_ID_ERROR,
    URL_REGEX
} from '../../constants/alternateIdConstants';
import { FETCH_POLICY_CONFIG } from '../../constants/constants';
import { searchClient, slimResultsQuery } from '../../list/slimQuery';
import { AlternateIdValidationInput } from '../AlternateIdForm';

export type ValidationError = {
    field: string;
    key: string;
    message: string;
};

/**
 * Fetches details of users that have conflicting information based on the provided entity and variables.
 *
 * @param entity - The entity type to search for conflicts (e.g., user, account).
 * @param variables - The variables to be used in the query for filtering results.
 * @param ownerId - The ID of the owner to be included in the request headers for context.
 * @returns A promise resolving to the result of the search query.
 */
export const getConflictedUserDetails = (entity, variables, ownerId) => {
    return searchClient.query({
        query: slimResultsQuery(entity),
        variables,
        fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy,
        context: {
            headers: {
                ownerId
            }
        }
    });
};

/**
 * Handles the response from fetching details of a conflicted entity based on the provided entity, email, and ownerId.
 * Calls the appropriate callback on success or error.
 *
 * @param entity - The entity identifier to fetch details for.
 * @param email - The email address associated with the entity.
 * @param ownerId - The owner ID to use in the request.
 * @param onSuccess - Callback function to be called with the response on successful fetch.
 * @param onError - Callback function to be called if an error occurs during fetch.
 */
export const handleFetchingConflictedEntityDetailsResponse = (
    entity: string,
    email: string | undefined,
    id: string | undefined,
    ownerId: string,
    onSuccess: (res) => void,
    onError: () => void
) => {
    const filters = id ? { identifier: 'identity', value: id } : { identifier: 'email', value: email };
    getConflictedUserDetails(
        entity,
        {
            page: { from: 0, size: 2000 },
            filters: [{ identifier: 'entity', value: entity }, filters]
        },
        ownerId
    )
        .then(onSuccess)
        .catch(onError);
};

/**
 * Generates an array of validation rule objects for alternate IDs based on the provided error key and field.
 *
 * @param key - The error key indicating the type of validation error (e.g., DOMAIN_ERROR, PROFILE_CONFLICT_ERROR, UNPARSABLE_ID_ERROR).
 * @param field - The name of the field being validated, used for constructing i18n tokens.
 * @returns An array containing a single validation rule object with an `isValid` function.
 *
 * The `isValid` function:
 * - Returns a localized error message if the key matches DOMAIN_ERROR or UNPARSABLE_ID_ERROR.
 * - Returns `false` if the key matches PROFILE_CONFLICT_ERROR.
 * - Returns `null` if no error key matches.
 */
const validateAlternateId = (key: string = undefined, field: string = undefined) => {
    return [
        {
            isValid: (prop, state, _isModified, resolveI18nToken) => {
                return key === DOMAIN_ERROR
                    ? resolveI18nToken(`alternateId.validation.${field}`)
                    : key === PROFILE_CONFLICT_ERROR
                      ? false
                      : key === UNPARSABLE_ID_ERROR
                        ? resolveI18nToken('alternateId.validation.unparsable.id.error')
                        : null;
            }
        }
    ];
};

/**
 * Handles validation errors for profile links by updating the validation rules and error key state.
 *
 * Iterates over the provided array of `ValidationError` objects. For each error, it updates the
 * corresponding field in the `validationRules` object using the `validateAlternateId` function.
 * If the error key matches `PROFILE_CONFLICT_ERROR`, it calls `validateAlternateId` with `undefined`
 * as the error key; otherwise, it passes the actual error key. It also updates the error key state
 * using the `setErrorKey` function.
 *
 * @param errors - An array of `ValidationError` objects representing the validation errors to handle.
 * @param field - The name of the field being validated.
 * @param validationRules - An object containing the validation rules for the form fields.
 * @param setErrorKey - A state setter function to update the current error key.
 */
const handleProfileLinksErrors = (
    errors: Array<ValidationError>,
    field: string,
    validationRules: ValidationRulesType,
    setErrorKey: Dispatch<SetStateAction<string>>
) => {
    errors.map((error: ValidationError) => {
        if (error.key === PROFILE_CONFLICT_ERROR) validationRules[field] = validateAlternateId(undefined, field);
        else validationRules[field] = validateAlternateId(error.key, field);
        setErrorKey(error.key);
    });
};

/**
 * Asynchronously fetches validation errors for a given alternate ID field, updates validation rules, and manages related state.
 *
 * This utility function is used to validate alternate ID fields (such as IMDb, LinkedIn, or Studio System IDs) in a form.
 * It checks the validity of the input, fetches validation errors from a remote source, updates dynamic validation rules,
 * and manages the state for conflicted entities, account existence, and error keys.
 *
 * @param fieldLabel - The label of the field being validated (e.g., "IMDb", "LinkedIn").
 * @param fieldState - The current value of the field being validated.
 * @param profileId - The profile ID associated with the validation context.
 * @param entityId - The entity ID associated with the validation context.
 * @param ownerId - The owner ID for the validation request.
 * @param getValidationErrors - A function that fetches validation errors for the given parameters.
 * @param setDynamicValidationRules - State setter for updating dynamic validation rules.
 * @param setState - State setter for updating the main form state.
 * @param setConflictedEntity - State setter for updating the conflicted entity (person or organization).
 * @param setErrorKey - State setter for updating the error key.
 *
 * @returns {Promise<void>} A promise that resolves when the validation process is complete.
 */
export const fetchResolveAndCreateValidationRules = async (
    fieldLabel: string,
    fieldState: string,
    entityId: string,
    ownerId: string,
    getValidationErrors: (input: AlternateIdValidationInput) => Promise<any>,
    setDynamicValidationRules: Dispatch<SetStateAction<ValidationRulesType>>,
    setState: Dispatch<SetStateAction<BasicInfoInputExtended | OrganizationEditorInput>>,
    setConflictedEntity: Dispatch<SetStateAction<Person | Organization>>,
    setErrorKey: Dispatch<SetStateAction<string>>,
    profileId?: string
) => {
    const isFieldIMDb: boolean = fieldLabel === IMDB_LABEL;
    const isFieldLinkedIn: boolean = fieldLabel === LINKEDIN_LABEL;
    const isFieldStudioSystem: boolean = fieldLabel === STUDIO_SYSTEM_LABEL;

    let validationRules: ValidationRulesType = {};

    const clearEntityState = (errorKey: string = undefined) => {
        setErrorKey(errorKey);
        setConflictedEntity(undefined);
        setState((prevState) => ({
            ...prevState,
            ...(isFieldIMDb && { imdbAlternateId: null }),
            ...(isFieldLinkedIn && { linkedInAlternateId: null }),
            ...(isFieldStudioSystem && { studioSystemAlternateId: null })
        }));
    };

    const setEntityState = (entity: Person | Organization, alternateId: AlternateIdInput) => {
        setState((prevState) => ({
            ...prevState,
            ...(isFieldIMDb && { imdbAlternateId: alternateId }),
            ...(isFieldLinkedIn && { linkedInAlternateId: alternateId }),
            ...(isFieldStudioSystem && { studioSystemAlternateId: alternateId })
        }));
        setConflictedEntity(entity);
    };

    if (fieldState) {
        const isFieldHostnameNotValid: boolean = checkLinkInInputField(fieldState, fieldLabel, validationRules);

        if (!isFieldHostnameNotValid) {
            const validationInput: AlternateIdValidationInput = {
                profileUrl: fieldState,
                profileId,
                entityId,
                ownerId
            };
            const response = await getValidationErrors(validationInput);

            const errors = response?.data?.searchSocialProfile?.errors;
            const alternateId = response?.data?.searchSocialProfile?.alternateId;
            const entity =
                response?.data?.searchSocialProfile?.person || response?.data?.searchSocialProfile?.organization;

            if (entity?.identity?.id && entity.identity.id === entityId) {
                clearEntityState();
                validationRules[fieldLabel] = null;
                setDynamicValidationRules((prevState) => ({ ...prevState, ...validationRules }));
                return;
            }

            //change entity's state after each call for validating the links
            setEntityState(entity, alternateId);

            //handle errors response
            errors && errors.length && handleProfileLinksErrors(errors, fieldLabel, validationRules, setErrorKey);
            if (!errors) {
                validationRules[fieldLabel] = null;
                setErrorKey(undefined);
            }
        } else clearEntityState(DOMAIN_ERROR);
    } else {
        clearEntityState();
        validationRules[fieldLabel] = null;
    }

    setDynamicValidationRules((prevState) => ({ ...prevState, ...validationRules }));
};

const createURLConstructor = (url: string) => {
    return url && URL_REGEX.test(url) ? new URL(url) : undefined;
};

const validateIMDB = () => {
    return [
        {
            isValid: (prop, state, _isModified, resolveI18nToken) => {
                const value = get(state, prop);
                const imdbURL = createURLConstructor(value);

                return IMDB_HOST === imdbURL?.hostname ? true : resolveI18nToken('alternateId.validation.Imdb');
            }
        }
    ];
};

const validateLinkedIn = () => {
    return [
        {
            isValid: (prop, state, _isModified, resolveI18nToken) => {
                const value = get(state, prop);
                const linkedInURL = createURLConstructor(value);
                const hostname = linkedInURL?.hostname?.replace(LINKEDIN_REGEX, '');

                return hostname === LINKEDIN_HOST ? true : resolveI18nToken('alternateId.validation.LinkedIn');
            }
        }
    ];
};

const validateStudioSystem = () => {
    return [
        {
            isValid: (prop, state, _isModified, resolveI18nToken) => {
                const value = get(state, prop);
                const studioSystemURL = createURLConstructor(value);
                const rootDomain = studioSystemURL?.hostname?.match(ROOT_DOMAIN_REGEX)?.[1];

                return rootDomain === STUDIO_SYSTEM_HOST
                    ? true
                    : resolveI18nToken('alternateId.validation.StudioSystem');
            }
        }
    ];
};

/**
 * Checks if the provided URL in the input field matches the expected hostname for the given field type
 * (IMDb, LinkedIn, or Studio System). If the URL does not match the expected hostname, updates the
 * validation rules for the current field with the appropriate validation function.
 *
 * @param currentUrlLink - The URL string entered in the input field.
 * @param currentActiveField - The label of the currently active input field (e.g., IMDb, LinkedIn, Studio System).
 * @param validationRules - An object containing validation rules for each field, which may be updated if the URL is invalid.
 * @returns `true` if the URL is in the wrong field (i.e., the hostname does not match the expected value), otherwise `false`.
 */
const checkLinkInInputField = (
    currentUrlLink: string,
    currentActiveField: string,
    validationRules: ValidationRulesType
) => {
    const isFieldIMDb: boolean = currentActiveField === IMDB_LABEL;
    const isFieldLinkedIn: boolean = currentActiveField === LINKEDIN_LABEL;
    const isFieldStudioSystem: boolean = currentActiveField === STUDIO_SYSTEM_LABEL;

    const urlConstructor = createURLConstructor(currentUrlLink);

    let hostname: string;
    if (isFieldIMDb) hostname = urlConstructor?.hostname;
    else if (isFieldLinkedIn) hostname = urlConstructor?.hostname?.replace(LINKEDIN_REGEX, '');
    else if (isFieldStudioSystem) hostname = urlConstructor?.hostname?.match(ROOT_DOMAIN_REGEX)?.[1];

    const isImdbInWrongField: boolean =
        isFieldIMDb && hostname !== IMDB_HOST && hostname !== IMDB_ME_HOST && hostname !== IMDB_PRO_HOST;
    const isLinkedInInWrongField: boolean = isFieldLinkedIn && hostname !== LINKEDIN_HOST;
    const isStudioSystemInWrongField: boolean = isFieldStudioSystem && hostname !== STUDIO_SYSTEM_HOST;

    if (isImdbInWrongField || isLinkedInInWrongField || isStudioSystemInWrongField) {
        const getValidationRule = () => {
            if (isImdbInWrongField) return validateIMDB();
            if (isLinkedInInWrongField) return validateLinkedIn();
            return validateStudioSystem();
        };
        validationRules[currentActiveField] = getValidationRule();
    }
    return isImdbInWrongField || isLinkedInInWrongField || isStudioSystemInWrongField;
};

/**
 * Adds a new alternate ID to the provided list, constructing a valid URL based on the profile ID and field label.
 *
 * @param listOfAlternateId - The array of existing alternate IDs to which the new alternate ID will be added.
 * @param alternateId - The alternate ID object to add to the list.
 * @param profileId - The profile identifier used to construct the alternate ID URL if provided.
 * @param fieldLabel - The label used to determine the URL path segment (e.g., 'IMDB_LABEL').
 * @returns The new length of the list after the alternate ID has been added.
 *
 * @remarks
 * If a `profileId` is provided, the function constructs a new URL using the origin from `alternateId.url` and appends
 * either 'name' or 'in' (depending on the `fieldLabel`) and the `profileId` to the path. The constructed URL replaces
 * the original `alternateId.url` in the new object added to the list.
 */
export const addAlternateId = (
    listOfAlternateId: Array<AlternateIdInput>,
    alternateId: AlternateIdInput,
    profileId: string,
    fieldLabel: string
) => {
    let validUrlLink: string;
    if (profileId) {
        const origin = createURLConstructor(alternateId.url)?.origin;
        validUrlLink = `${origin}/${fieldLabel === IMDB_LABEL ? 'name' : 'in'}/${profileId}`;
    }
    return listOfAlternateId.push({ ...alternateId, url: validUrlLink || alternateId.url });
};

export const findAlternateId = (alternateIds: AlternateIdInput[], label: string) => {
    return alternateIds.find((el) => el.label === label);
};
